/* eslint-disable implicit-arrow-linebreak */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Icon,
  Loading,
  Selection,
  Tooltip,
} from '@folio/stripes-components';
import { Field, useField } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { isEqual } from 'lodash';
import { useStripes } from '@folio/stripes/core';
import { Layout } from '@folio/stripes/components';
import FormFieldsGenerator from '../../../../../shared/FormFieldsGenerator';
import EmptyData from '../../../../../shared/EmptyData';
import ConfirmChangeTemplateTypeModal from './ConfirmChangeTemplateTypeModal';

const propTypes = {
  template: PropTypes.object,
  loading: PropTypes.bool,
  actionType: PropTypes.string,
  setTemplateId: PropTypes.func,
  originTemplateId: PropTypes.string,
  controlledVocab: PropTypes.object,
};

function InstanceData({
  template,
  loading,
  actionType,
  setTemplateId,
  originTemplateId,
  controlledVocab,
}) {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const templateField = useField('instanceData.templateTypeId');
  const templates = controlledVocab.templates;
  const stripes = useStripes();
  const history = useHistory();
  const intl = useIntl();

  const onChangeTemplateType = () => {
    if (actionType === 'create') {
      history.replace(
        `/inventory/instance-wizard/create/${templateField.input.value}`,
      );
      setOpenConfirmModal(false);
    } else {
      setTemplateId(templateField.input.value);
      setOpenConfirmModal(false);
    }
  };

  const onCancelChangeTemplateType = () => {
    templateField.input.onChange(templateField.meta.initial);
    setOpenConfirmModal(false);
  };

  if (loading) {
    return (
      <Layout
        className="centered full"
        style={{ maxWidth: '15rem', height: '8rem' }}
      >
        &nbsp;
        <Loading size="xlarge" />
      </Layout>
    );
  }

  if (!template?.templateFields?.instance.length) {
    return (
      <EmptyData
        message={
          <FormattedMessage
            id="ui-plugin-inventory-templates.instanceWizard.form.page.noFields"
            defaultMessage="No fields found."
          />
        }
      />
    );
  }

  return (
    <div style={{ paddingLeft: '200px', paddingRight: '200px' }}>
      {!loading && (
        /** Change template type on the fly */
        <>
          <ConfirmChangeTemplateTypeModal
            open={openConfirmModal}
            onClose={onCancelChangeTemplateType}
            onChangeTemplateType={onChangeTemplateType}
          />
          <OnChange name="instanceData.templateTypeId">
            {(value) => {
              if (
                value &&
                template.id &&
                !loading &&
                !isEqual(template.id, value)
              ) {
                setOpenConfirmModal(true);
              }
            }}
          </OnChange>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Field
              name="instanceData.templateTypeId"
              component={Selection}
              dataOptions={[
                { label: '', value: '' },
                ...templates
                  .sort((a, b) => b.isDefault - a.isDefault)
                  .map((temp) => ({
                    label: temp.name?.find(
                      (field) => field?.locale === intl.locale,
                    )?.value,
                    value: temp.id,
                  })),
              ]}
              fullWidth
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.instanceWizard.instanceData.fields.templateType.label"
                  defaultMessage="Template type"
                />
              }
              disabled={
                !stripes.hasPerm(
                  'ui-plugin-inventory-templates.inventoryTemplates.changeInstanceTemplateType',
                )
              }
            />

            {actionType !== 'create' && (
              <div style={{ marginTop: '1.79rem', padding: '0 20px' }}>
                <Tooltip
                  id="instanceWizard-instanceData-reset-template-type-tooltip"
                  text={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.instanceWizard.instanceData.resetTemplateType.tooltip.msg"
                      defaultMessage="Reset instance template type"
                    />
                  }
                >
                  {({ ref, ariaIds }) => (
                    <Button
                      buttonStyle="slim"
                      onClick={() =>
                        templateField.input.onChange(originTemplateId)
                      }
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={templateField.input.value === originTemplateId}
                      aria-labelledby={ariaIds.text}
                      ref={ref}
                    >
                      <Icon icon="refresh" size="large" />
                    </Button>
                  )}
                </Tooltip>
              </div>
            )}
          </div>
          <FormFieldsGenerator
            key="instance-template-page"
            formValues={template?.templateFields?.instance}
            pageName="instanceData"
            controlledVocab={controlledVocab}
            actionType={actionType}
          />
        </>
      )}
    </div>
  );
}

InstanceData.propTypes = propTypes;

export default InstanceData;
